import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import './App.css';
import {
  CALENDLY_URL,
  QUOTE_MAILTO,
  Header,
  Footer,
  WorkflowSection,
  PricingSection,
  FaqSection,
  FinalCta,
  useRevealObserver,
} from './sections';

const STACK_LOGOS = [
  { name: 'Unity', type: 'engine' },
  { name: 'Unreal', type: 'engine' },
  { name: 'Godot', type: 'engine' },
  { name: 'GitHub', type: 'vcs' },
  { name: 'Perforce', type: 'vcs' },
  { name: 'UVC', type: 'vcs' },
] as const;

function App(): JSX.Element {
  useRevealObserver();

  const scrollToSection = useCallback((sectionId: string): void => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="splash-root">
      <Helmet defer={false}>
        <title>Loten Labs: Game Localization Grounded in Your Gameplay</title>
        <meta name="description" content="Context-aware game localization for Unity, Unreal, Godot. We ingest your playtest video, lock characters and tone, push to your repo in 30+ languages." />
        <link rel="canonical" href="https://useloten.com/" />
        <meta property="og:title" content="Loten Labs: Game Localization Grounded in Your Gameplay" />
        <meta property="og:description" content="Context-aware game localization for Unity, Unreal, Godot. We ingest your playtest video, lock characters and tone, push to your repo in 30+ languages." />
        <meta property="og:url" content="https://useloten.com/" />
        <meta name="twitter:title" content="Loten Labs: Game Localization Grounded in Your Gameplay" />
        <meta name="twitter:description" content="Context-aware game localization for Unity, Unreal, Godot. We ingest your playtest video, lock characters and tone, push to your repo in 30+ languages." />
      </Helmet>
      <a className="skip-link" href="#main-content">Skip to main content</a>

      <Header onNavClick={scrollToSection} />

      <main id="main-content">
        {/* ===== Hero Section ===== */}
        <section className="hero" aria-label="Hero — localization grounded in your gameplay">
          <div className="container">
            <h1 className="reveal">
              Game localization grounded in your{' '}
              <span className="hero-highlight">actual gameplay</span>.
            </h1>
            <p className="hero-subtitle reveal">
              Linguist-reviewed. Pushed to your repo. Without the producer-month.
            </p>
            <div className="hero-cta reveal">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="cta-button cta-primary" id="hero-cta">
                <span>Book a Demo</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              <a href={QUOTE_MAILTO} className="cta-button cta-secondary" id="hero-cta-quote">
                <span>Get a Quote</span>
              </a>
            </div>
          </div>
        </section>

        {/* ===== Trust Band ===== */}
        <section className="trust-band reveal" aria-label="Works with your stack, IP promise, and scale">
          <div className="container">
            <div className="trust-band__grid">
              <div className="trust-band__slot">
                <span className="trust-band__label">Works with</span>
                <div className="trust-band__logos">
                  {STACK_LOGOS.map((logo) => (
                    <span key={logo.name} className={`trust-logo trust-logo--${logo.type}`}>
                      {logo.name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="trust-band__slot trust-band__slot--center">
                <span className="trust-band__label">Flat rate</span>
                <a
                  href="#pricing"
                  className="trust-band__promise"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('pricing');
                  }}
                >
                  <strong className="trust-band__price">
                    <span className="trust-band__price-num">$0.10</span>
                    <span className="trust-band__price-unit">/word</span>
                  </strong>
                  <span className="trust-band__promise-sub">14-day turnaround. No setup fees.</span>
                </a>
              </div>
              <div className="trust-band__slot">
                <span className="trust-band__label">Scale</span>
                <ul className="trust-band__bullets">
                  <li>30+ languages</li>
                  <li>Reviewed by native-speaking linguists</li>
                  <li>Repo-native pull requests</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <WorkflowSection />

        {/* ===== Why Context Changes Everything ===== */}
        <section id="why-context" className="why-context" aria-label="Why context changes everything">
          <div className="container">
            <div className="why-context__header reveal">
              <h2 className="section-title">
                Why context <span className="title-accent">changes everything</span>
              </h2>
              <p className="section-subtitle">
                The same English string, translated two different ways.
              </p>
            </div>

            <div className="compare reveal">
              <div className="compare__col compare__col--bad">
                <div className="compare__tag">Spreadsheet localization</div>
                <div className="compare__row">
                  <span className="compare__lang">EN</span>
                  <span className="compare__string">"Fire!"</span>
                </div>
                <div className="compare__context compare__context--missing">
                  No scene. No speaker. No glossary.
                </div>
                <div className="compare__divider" aria-hidden="true" />
                <div className="compare__row compare__row--result">
                  <span className="compare__lang">JA</span>
                  <span className="compare__string">"火事だ！"</span>
                </div>
                <div className="compare__note">
                  The linguist picked the most common meaning. The game ships with a fire alarm where a cannon order should be.
                </div>
              </div>

              <div className="compare__col compare__col--good">
                <div className="compare__tag">Loten localization</div>
                <div className="compare__row">
                  <span className="compare__lang">EN</span>
                  <span className="compare__string">"Fire!"</span>
                </div>
                <div className="compare__context">
                  <span className="compare__context-label">Scene</span>
                  Ship captain, broadside battle, cannons loaded. Glossary marks
                  this string as a combat order.
                </div>
                <div className="compare__divider" aria-hidden="true" />
                <div className="compare__row compare__row--result">
                  <span className="compare__lang">JA</span>
                  <span className="compare__string">"撃て！"</span>
                </div>
                <div className="compare__note">
                  The linguist saw the frame and translated the order, not the noun. Lands on the first try.
                </div>
              </div>
            </div>

            <div className="why-context__body reveal">
              <p>
                One string is embarrassing. Across 30,000 strings and eight languages, wrong guesses accumulate and ship. Negative Steam reviews in Simplified Chinese, Japanese, and Brazilian Portuguese. Retranslation spreadsheets. A region that should have been a third of your revenue becomes a liability.
              </p>
              <p>
                We map every string to the frame it appears in and build a style guide from that context. Characters sound like themselves in every language, and lines land right the first time.
              </p>
              <p className="why-context__kicker">
                This is the single most important thing we do.
              </p>
            </div>
          </div>
        </section>

        <PricingSection />
        <FaqSection />
        <FinalCta />
      </main>

      <Footer />
    </div>
  );
}

export default App;
